import { Analytics, FxIdDomainStoreEnumsSupportedWebPublishingPlatform } from "@fxid/openapi-client";
import OpenApiClient from "@fxid/openapi-client";
import { FxIdSdkAdapterSocialSettingDefault, FxIdSdkAdapterStickyBannerShowErrorReason } from "@fxid/sdk-core";
import log, { exitFullscreen, findAdapterConfig, getDocUrl } from "@fxid/shared";
import i18next from "i18next";
import { IframeTypeToSupportedPlatformMap } from "../constants";
export class FxIdSdkBaseAdapter {
    constructor(exportedSdk, game, config, dependencies) {
        this.exportedSdk = exportedSdk;
        this.game = game;
        this.config = config;
        this.defaultSocialSettings = FxIdSdkAdapterSocialSettingDefault;
        this._dependencies = dependencies;
    }
    async SocialSettings() {
        const adapterSettings = findAdapterConfig(this.config.PublicWebClientConfig.Adapters, this.adapter);
        const adapterUiDisabled = adapterSettings?.DisableHeaderUi === true;
        const settings = {
            ...this.defaultSocialSettings
        };
        if (adapterUiDisabled) {
            settings.ui.disabled = true;
        }
        return Promise.resolve(settings);
    }
    /**
     * Вызывается после успешной аутентификации пользователя.
     * Переопределяется в конкретных адаптерах для выполнения дополнительной инициализации,
     * которая требует авторизованного пользователя (например, проверка непотребленных покупок).
     */
    OnAfterLogin() {
        return Promise.resolve();
    }
    RegisterIframeWindow() {
        return Promise.resolve();
    }
    CanReview() {
        return Promise.resolve({ canReview: false });
    }
    RequestReview() {
        return Promise.resolve({ shown: false });
    }
    ReviewInfo() {
        return Promise.resolve({ isRatingAvailable: false });
    }
    StoreAdditionalData() {
        return Promise.resolve(undefined);
    }
    AdsIsVideoReady() {
        // ignore
        return Promise.resolve(false);
    }
    AdsShowVideo() {
        // ignore
        return Promise.resolve();
    }
    AdsStickyBannerShow() {
        return Promise.resolve({
            success: false,
            errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
        });
    }
    AdsIsInterstitialReady() {
        // ignore
        return Promise.resolve(false);
    }
    AdsShowInterstitial() {
        // ignore
        return Promise.resolve();
    }
    AdsStickyBannerHide() {
        return Promise.resolve({
            success: false,
            errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
        });
    }
    GetFriends(_request) {
        return Promise.resolve({ friends: [] });
    }
    InviteFriends(_request) {
        return Promise.resolve({ friends: [] });
    }
    AuthenticateUser(_request) {
        return Promise.resolve({});
    }
    JoinCommunity(_request) {
        return Promise.resolve({ success: false });
    }
    AddToFavorites(_request) {
        return Promise.resolve({ success: false });
    }
    WordsFilter(request) {
        return Promise.resolve({ success: true });
    }
    RateApplication(request) {
        return Promise.resolve({ success: true });
    }
    IsFocusChanged() {
        const eventHandler = () => {
            // Проверяем, скрыта ли страница
            if (document.hidden) {
                this.exportedSdk.DispatchFocusChanged({ focus: false });
            }
            else {
                this.exportedSdk.DispatchFocusChanged({ focus: true });
            }
        };
        document.addEventListener("visibilitychange", eventHandler);
    }
    async LoadingReady() {
        log.info("Called LoadingReady api");
        return Promise.resolve();
    }
    async GameStarted() {
        log.info("Called GameStarted api");
        return Promise.resolve();
    }
    async GameStopped() {
        log.info("Called GameStopped api");
        return Promise.resolve();
    }
    /**
     * Перенаправляет некоторые URL-адреса на внутренние ресурсы или специальные страницы.
     * Это временное решение для обработки специфичных ссылок.
     * @param url - исходный URL для возможного перенаправления.
     * @returns измененный или оригинальный URL.
     */
    async __HACK_RemapUrl(url) {
        if (import.meta.env.VITE_APP_TENANT_OVERRIDE === "" || import.meta.env.VITE_APP_TENANT_OVERRIDE === "FX") {
            if (url.toString().startsWith("https://fxgames.zendesk.com/hc") ||
                url.toString().match(/\/docs\/(.*)\/faq.html/)) {
                // log.info("Remapping to freshdesk");
                // return new URL("https://fxgames.freshdesk.com/support/solutions");
                log.info("Remapping to faq.txt");
                const baseUrl = import.meta.env.BASE_URL || '';
                const lang = i18next.language;
                const docUrl = getDocUrl({
                    docFile: "faq.html",
                    adapter: this.adapter
                }, baseUrl, lang);
                return docUrl;
            }
            if (url.toString().startsWith("https://discord")) {
                log.info("Remapping discrod url");
                const url = new URL(`${import.meta.env.BASE_URL}/discord-qr`, window.location.origin);
                url.searchParams.append("game", this.game);
                return url;
            }
            if (url.toString().startsWith("https://fx.gl/terms-of-service")) {
                log.info("Remapping tos url");
                const baseUrl = import.meta.env.BASE_URL || '';
                const lang = i18next.language;
                const docUrl = getDocUrl({
                    docFile: "tos.txt",
                    adapter: this.adapter
                }, baseUrl, lang);
                return docUrl;
            }
            if (url.toString().startsWith("https://fx.gl/privacy-policy")) {
                log.info("Remapping pp url");
                const baseUrl = import.meta.env.BASE_URL || '';
                const lang = i18next.language;
                const docUrl = getDocUrl({
                    docFile: "pp.txt",
                    adapter: this.adapter
                }, baseUrl, lang);
                return docUrl;
            }
        }
        return url;
    }
    async OpenModal(requestJson) {
        log.info("Opening modal %o", requestJson);
        const request = JSON.parse(requestJson);
        const url = new URL(request.Url);
        if ((this.game === "gc" || this.game == "wizqgc") &&
            (url.href.toLowerCase().includes("tos") ||
                url.href.toLowerCase().includes("pp") ||
                url.href.toLowerCase().includes("terms-of-service") ||
                url.href.toLowerCase().includes("privacy-policy"))) {
            try {
                const allDocumentsResponse = await OpenApiClient.Games.fxIdWebFeaturesDocumentsGetAllDocumentsWithVersionsGetAllDocumentsWithVersionsEndpoint({
                    GameSystemName: this.game
                });
                let documentToShow;
                const lowerCaseUrl = url.href.toLowerCase();
                if (lowerCaseUrl.includes("tos") || lowerCaseUrl.includes("terms-of-service")) {
                    documentToShow = allDocumentsResponse.Documents.find((doc) => doc.DocumentType.Value === "TOS");
                }
                else if (lowerCaseUrl.includes("pp") || lowerCaseUrl.includes("privacy-policy")) {
                    documentToShow = allDocumentsResponse.Documents.find((doc) => doc.DocumentType.Value === "PP");
                }
                if (documentToShow && documentToShow.Versions.length > 0) {
                    await exitFullscreen();
                    // TODO: Implement document acceptance modal through dependencies
                    // This requires a new callback in IFxIdSdkDependencies
                    log.warn("Document acceptance modal not implemented in SDK independence mode");
                    // Fallthrough to default iframe modal behavior
                }
            }
            catch (error) {
                log.error("Failed to fetch or show document:", error);
                // Fallback to default behavior in case of an error
            }
        }
        await exitFullscreen();
        const remappedUrl = await this.__HACK_RemapUrl(url);
        if (this.adapter !== "fxid") {
            const platform = IframeTypeToSupportedPlatformMap[this.adapter];
            remappedUrl.searchParams.append("social", FxIdDomainStoreEnumsSupportedWebPublishingPlatform[platform]);
        }
        this._dependencies.openIframeModal({ url: remappedUrl.toString() });
        const sessionData = this._dependencies.getSessionData();
        void Analytics.sdk.openIframeModal({
            game: this.game,
            adapter: this.adapter,
            url: remappedUrl.toString()
        }, sessionData);
        return Promise.resolve({ Success: true });
    }
}
